import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { Chronology, ChronologyEntry } from 'components/DetailsView/Chronology';
import { Loader, LoaderModeType } from 'components/Loader';
import { WarningEntry } from 'components/WarningIndicator';

import { colors } from 'constants/colors';
import { dateFromString, formatDateAsAnniversary, formatDateForDisplay } from 'helpers/dateTimes';

import {
    GetSupplierDeliveryHistoryQuery,
    SupplyOrderState,
    useGetSupplierDeliveryHistoryQuery,
} from 'data/__generated__';

import { StatusTag } from '../../components/StatusTag';
import { SupplyOrderDetails } from '../supplyOrderFormValues';

const BLOCK_TITLE = 'Chronologie des livraisons du fournisseur';

type HistoryEntry = GetSupplierDeliveryHistoryQuery['supplierDeliveryHistory'][number];

const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;

/** Nombre de jours entre deux dates de calendrier. `null` dès qu'une des deux manque. */
function daysBetween(from?: string | null, to?: string | null): number | null {
    if (!from || !to) {
        return null;
    }
    return Math.round((dateFromString(to).getTime() - dateFromString(from).getTime()) / MILLISECONDS_PER_DAY);
}

/**
 * Ce fournisseur tient-il ses dates ?
 *
 * Trois dates par achat : quand on a commandé, quand il avait promis de livrer (la PREMIÈRE date
 * planifiée, pas la dernière — sinon une replanification effacerait le retard), et quand il a
 * effectivement livré. La frise place chaque achat à sa date de livraison et colore le point
 * par sa ponctualité : la fiabilité du fournisseur se lit d'un coup d'œil, sans dérouler un tableau.
 *
 * Rendue dans la carte d'en-tête de la fiche (`SupplyOrderKeyFacts`), donc sans carte ni titre de
 * section à elle : elle se contente d'un filet de séparation et d'un libellé au format des faits.
 */
export const SupplierDeliveryChronology = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{BLOCK_TITLE}</span>
        <SupplierDeliveryChronologyContent supplyOrder={supplyOrder} />
    </div>
);

const SupplierDeliveryChronologyContent = ({ supplyOrder }: { supplyOrder: SupplyOrderDetails }) => {
    const { data, loading, error } = useGetSupplierDeliveryHistoryQuery({
        variables: { supplierId: supplyOrder.supplier._id },
    });

    const entries = data?.supplierDeliveryHistory;

    // Un achat sans aucune date ne peut pas être placé sur une frise.
    const { datedEntries, chronologyEntries } = useMemo(() => {
        const dated = (entries ?? []).filter((entry) => !!getTimelineDate(entry));

        return {
            datedEntries: dated,
            chronologyEntries: dated.map<ChronologyEntry>((entry) => {
                const { color, labelColor, label } = getPunctuality(entry);

                return {
                    id: entry._id,
                    date: getTimelineDate(entry) ?? '',
                    color,
                    labelColor,
                    stateLabel: label,
                    warnings: getStorageWarnings(entry),
                    meta: (
                        <span>
                            n°{entry.number ?? '—'} · commandé le {renderDate(entry.orderDate)} · promis le{' '}
                            {renderDate(entry.initialDeliveryDate)}
                        </span>
                    ),
                };
            }),
        };
    }, [entries]);

    if (loading) {
        return (
            <div className="flex h-20 w-full items-center justify-center">
                <Loader mode={LoaderModeType.Spin} />
            </div>
        );
    }

    if (error) {
        return <p className="text-sm text-destructive">Erreur lors du chargement de l'historique du fournisseur.</p>;
    }

    if (!chronologyEntries.length) {
        return <p className="text-sm text-muted-foreground">Aucun achat daté chez ce fournisseur.</p>;
    }

    return (
        <div className="flex flex-col gap-3">
            <SummaryLine entries={entries ?? []} />
            <Chronology
                entries={chronologyEntries}
                currentEntryId={supplyOrder._id}
                groupBy="month"
                renderEntryDetails={({ id }) => {
                    const selectedEntry = datedEntries.find(({ _id }) => _id === id);
                    return selectedEntry ? <HistoryCard entry={selectedEntry} /> : null;
                }}
            />
        </div>
    );
};

/** Les quatre chiffres de ponctualité, sur une seule ligne : la frise doit rester légère. */
const SummaryLine = ({ entries }: { entries: HistoryEntry[] }) => {
    const delays = entries
        .map(({ initialDeliveryDate, deliveryDate }) => daysBetween(initialDeliveryDate, deliveryDate))
        .filter((delay): delay is number => delay !== null);
    const leadTimes = entries
        .map(({ orderDate, deliveryDate }) => daysBetween(orderDate, deliveryDate))
        .filter((leadTime): leadTime is number => leadTime !== null);
    const lateDelays = delays.filter((delay) => delay > 0);

    return (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
            <Stat value={leadTimes.length ? `${average(leadTimes)} j` : '—'} label="de délai moyen" />
            <Separator />
            <Stat
                value={delays.length ? `${delays.filter((delay) => delay <= 0).length}/${delays.length}` : '—'}
                label="livrés à la date promise"
            />
            <Separator />
            <Stat value={lateDelays.length ? `+${average(lateDelays)} j` : '—'} label="de retard moyen" />
            <Separator />
            <Stat value={String(entries.length)} label="achats analysés (annulés exclus)" />
        </div>
    );
};

const Stat = ({ value, label }: { value: string; label: string }) => (
    <span>
        <span className="font-semibold text-foreground">{value}</span> {label}
    </span>
);

const Separator = () => <span className="text-border">|</span>;

/** La carte affichée sous la frise quand on sélectionne un autre achat que celui de la page. */
const HistoryCard = ({ entry }: { entry: HistoryEntry }) => {
    const { _id, number, state, orderDate, initialDeliveryDate, scheduledDeliveryDate, deliveryDate } = entry;
    const { color } = getPunctuality(entry);

    return (
        <Link
            to={`/supply-order/${_id}`}
            className="flex items-center gap-3 overflow-hidden rounded-lg border border-border bg-card px-3 py-2 transition-colors hover:border-foreground/30"
        >
            <span className="w-1 shrink-0 self-stretch rounded" style={{ backgroundColor: color }} />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span className="font-semibold">Achat n°{number ?? '—'}</span>
                <span className="text-xs text-muted-foreground">
                    Commandé le {renderDate(orderDate)} · promis le {renderDate(initialDeliveryDate)} ·{' '}
                    {deliveryDate
                        ? `livré le ${renderDate(deliveryDate)}`
                        : `prévu le ${renderDate(scheduledDeliveryDate)}`}
                </span>
            </div>
            <StatusTag state={state} />
        </Link>
    );
};

/**
 * Ponctualité d'un achat : vert s'il est arrivé au plus tard à la date promise, rouge sinon, gris
 * tant qu'il n'est pas livré. C'est cette couleur qui porte l'information sur la frise.
 */
function getPunctuality(entry: HistoryEntry): { color: string; labelColor?: string; label: string } {
    const { initialDeliveryDate, scheduledDeliveryDate, deliveryDate } = entry;

    if (!deliveryDate) {
        const replanned = daysBetween(initialDeliveryDate, scheduledDeliveryDate);
        return {
            color: colors.lightGrey,
            labelColor: colors.black,
            label: replanned && replanned > 0 ? `Livraison replanifiée +${replanned} j` : 'Livraison prévue',
        };
    }

    const delay = daysBetween(initialDeliveryDate, deliveryDate);

    if (delay === null) {
        return { color: colors.blandBlue, labelColor: colors.black, label: 'Livré, sans date promise' };
    }
    if (delay > 0) {
        return { color: colors.red, label: `+${delay} j de retard` };
    }
    if (delay === 0) {
        return { color: colors.green, labelColor: colors.black, label: 'À l’heure' };
    }
    return { color: colors.green, labelColor: colors.black, label: `${-delay} j d’avance` };
}

/**
 * Un achat livré doit être rangé le jour même. Repère au-dessus du point sinon : rangé avec retard,
 * ou pas encore rangé alors que la livraison date d'avant aujourd'hui. Un achat rangé sans trace de
 * rangement dans son historique (données anciennes) ne dit rien : pas de repère.
 */
function getStorageWarnings({ state, deliveryDate, storedDate }: HistoryEntry): WarningEntry[] {
    if (!deliveryDate) {
        return [];
    }

    if (storedDate) {
        const storageDelay = daysBetween(deliveryDate, storedDate);
        return storageDelay && storageDelay > 0
            ? [
                  {
                      level: 'warning',
                      message: `Rangé ${storageDelay} j après la livraison (le ${renderDate(storedDate)})`,
                  },
              ]
            : [];
    }

    if (state === SupplyOrderState.InRack) {
        return [];
    }

    const daysSinceDelivery = daysBetween(deliveryDate, formatDateAsAnniversary({ dateTime: new Date() }));
    return daysSinceDelivery && daysSinceDelivery > 0
        ? [{ level: 'warning', message: `Pas encore rangé, livré il y a ${daysSinceDelivery} j` }]
        : [];
}

/** La date qui situe l'achat sur la frise : celle de la livraison, réelle à défaut prévue. */
function getTimelineDate({
    orderDate,
    initialDeliveryDate,
    scheduledDeliveryDate,
    deliveryDate,
}: HistoryEntry): string | null {
    return deliveryDate ?? scheduledDeliveryDate ?? initialDeliveryDate ?? orderDate ?? null;
}

function renderDate(date?: string | null): string {
    return date ? formatDateForDisplay(date) : '—';
}

function average(values: number[]): number {
    return Math.round((values.reduce((total, value) => total + value, 0) / values.length) * 10) / 10;
}
